import React, { useState, useEffect, useCallback } from 'react';
import ConnectionChatPanel from '../layout/ConnectionPanel';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseConfig'; // Adjust path to your Firebase config


/**
 * Networking editor component with connections list and chat area
 * @param {Object} props
 * @param {boolean} props.isOnPhone - Whether the user is on a phone device
 * @param {function} props.onClose - Function to close the networking editor
 * @returns {JSX.Element} The networking editor component
 */
const NetworkingEditor = ({ isOnPhone, onClose }) => {
  // Set up our global flag if it doesn't exist
  if (typeof window !== 'undefined') {
    window.__LINKEDIN_AUTH_STATES = window.__LINKEDIN_AUTH_STATES || {};
  }

  // Base URL for the consolidated LinkedIn API endpoints
  const API_BASE_URL = 'https://us-central1-middgpt.cloudfunctions.net/linkedInApi';

  // Force update mechanism
  const [, forceRender] = useState({});
  const forceUpdate = useCallback(() => forceRender({}), []);

  // Authentication and verification states
  const [isLinkedInConnected, setIsLinkedInConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  const [linkedInProfile, setLinkedInProfile] = useState(null);
  const [error, setError] = useState(null);

  // Alumni and connections states
  const [alumni, setAlumni] = useState([]);
  const [isLoadingAlumni, setIsLoadingAlumni] = useState(false);
  const [connections, setConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState(null);

  // Tab state for the main content area
  const [activeTab, setActiveTab] = useState('connections'); // 'connections' or 'alumni'

  // Alumni categories
  const categories = [
    'technology', 'finance', 'healthcare', 'education',
    'marketing', 'entrepreneurship', 'consulting', 'nonprofit',
    'legal', 'engineering'
  ];
  const [selectedCategory, setSelectedCategory] = useState('technology');

  // Update these functions in your NetworkingEditor component
  const storeLinkedInToken = async (accessToken, expiresIn, userId) => {
    try {
      // Include userId if available
      const body = {
        accessToken,
        expiresIn
      };

      if (userId) {
        body.userId = userId;
      }

      // Make sure we're using the linkedInApi function, not api
      const response = await fetch(`${API_BASE_URL}/api/linkedin/store-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      console.log("Token storage response:", response.status, response.ok);

      if (response.ok) {
        console.log("LinkedIn token stored in backend successfully");
        return true;
      }
      console.warn("Failed to store LinkedIn token in backend, but storing locally");

      // As a fallback, store token details in localStorage
      localStorage.setItem('linkedin_access_token', accessToken);
      localStorage.setItem('linkedin_token_expires', Date.now() + (expiresIn * 1000));

      return true; // Return true anyway so the flow continues
    } catch (error) {
      console.error("Error storing LinkedIn token:", error);

      // Still store locally as fallback
      localStorage.setItem('linkedin_access_token', accessToken);
      localStorage.setItem('linkedin_token_expires', Date.now() + (expiresIn * 1000));

      return true; // Return true so the UI flow continues
    }
  };

  const checkLinkedInTokenStatus = async () => {
    try {
      // Try the server endpoint first
      const response = await fetch(`${API_BASE_URL}/api/linkedin/token-status`);

      console.log("Token status response:", response.status, response.ok);

      if (response.ok) {
        const data = await response.json();
        console.log("Token status data:", data);
        return data;
      }

      // If server check fails, fall back to checking localStorage
      console.log("Server token check failed, checking localStorage");
      const localToken = localStorage.getItem('linkedin_access_token');
      const localExpiry = localStorage.getItem('linkedin_token_expires');

      if (localToken && localExpiry) {
        const isExpired = Date.now() > parseInt(localExpiry);
        return {
          exists: true,
          isExpired,
          expiresAt: new Date(parseInt(localExpiry)).toISOString()
        };
      }

      return { exists: false, isExpired: true };
    } catch (error) {
      console.error("Error checking LinkedIn token status:", error);

      // Check localStorage as fallback
      const localToken = localStorage.getItem('linkedin_access_token');
      const localExpiry = localStorage.getItem('linkedin_token_expires');

      if (localToken && localExpiry) {
        const isExpired = Date.now() > parseInt(localExpiry);
        return {
          exists: true,
          isExpired,
          expiresAt: new Date(parseInt(localExpiry)).toISOString()
        };
      }

      return { exists: false, isExpired: true };
    }
  };

  const verifyFirestoreToken = async () => {
    try {
      // Try the server endpoint first
      const response = await fetch(`${API_BASE_URL}/api/linkedin/verify-token`);

      console.log("Token verification response:", response.status, response.ok);

      if (response.ok) {
        const data = await response.json();
        console.log("Token verification data:", data);
        return data.verified;
      }

      // If server verification fails, fall back to checking localStorage
      console.log("Server token verification failed, checking localStorage");
      const localToken = localStorage.getItem('linkedin_access_token');
      const localExpiry = localStorage.getItem('linkedin_token_expires');

      if (localToken && localExpiry) {
        const isExpired = Date.now() > parseInt(localExpiry);
        return !isExpired; // If token exists and isn't expired, consider it verified
      }

      return false;
    } catch (error) {
      console.error("Error verifying LinkedIn token in Firestore:", error);

      // Check localStorage as fallback
      const localToken = localStorage.getItem('linkedin_access_token');
      const localExpiry = localStorage.getItem('linkedin_token_expires');

      if (localToken && localExpiry) {
        const isExpired = Date.now() > parseInt(localExpiry);
        return !isExpired; // If token exists and isn't expired, consider it verified
      }

      return false;
    }
  };

  // Ensure we have a profile to display
  const ensureLinkedInProfile = useCallback(() => {
    if (!linkedInProfile) {
      setLinkedInProfile({
        firstName: 'LinkedIn',
        lastName: 'User',
        profilePicture: null,
        headline: 'Connected User'
      });
    }
  }, [linkedInProfile]);

  // Load alumni data from backend
  const loadAlumni = async (category) => {
  setIsLoadingAlumni(true);
  try {
    // Create a reference to the alumni collection
    const alumniRef = collection(db, 'alumni');

    // Build query based on category
    let alumniQuery;
    if (category && category !== 'all') {
      // Query for specific category
      alumniQuery = query(alumniRef, where('category', '==', category));
    } else {
      // Get all alumni if no category is specified
      alumniQuery = alumniRef;
    }

    // Execute the query
    const querySnapshot = await getDocs(alumniQuery);

    // Process the results
    const alumniData = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      alumniData.push({
        id: doc.id,
        firstName: data.firstName || 'Unknown',
        lastName: data.lastName || 'Alumni',
        headline: data.headline || data.profession || 'Middlebury Alumni',
        industry: data.industry || category || 'Various',
        pictureUrl: data.pictureUrl || data.photoURL || null,
        publicProfileUrl: data.publicProfileUrl || null,
        location: data.location || 'United States',
        category: data.category || category || 'general',
        school: data.school || 'Middlebury College',
        fromAPI: false
      });
    });

    // Update state with the alumni data
    setAlumni(alumniData);

    // If no results were found and we have a category, try a fallback
    if (alumniData.length === 0 && category) {
      console.log(`No alumni found for category: ${category}. Using fallback data.`);
      setAlumni(generateMockAlumni(category));
    }
  } catch (error) {
    console.error("Error loading alumni from Firestore:", error);
    // Use mock data as a fallback on error
    setAlumni(generateMockAlumni(category));
  } finally {
    setIsLoadingAlumni(false);
  }
};

  // Generate mock alumni data as fallback
  const generateMockAlumni = (category) => {
    const categoryTitles = {
      'technology': ['Software Engineer', 'Data Scientist', 'Product Manager', 'CTO'],
      'finance': ['Financial Analyst', 'Investment Banker', 'Portfolio Manager', 'CFO'],
      'healthcare': ['Physician', 'Nurse Practitioner', 'Healthcare Administrator', 'Medical Researcher'],
      'education': ['Professor', 'Teacher', 'Education Director', 'Academic Dean'],
      'marketing': ['Marketing Director', 'Brand Manager', 'Digital Marketer', 'CMO'],
      'entrepreneurship': ['Founder', 'CEO', 'Serial Entrepreneur', 'Startup Advisor'],
      'consulting': ['Management Consultant', 'Strategy Consultant', 'Business Analyst', 'Advisory Lead'],
      'nonprofit': ['Nonprofit Director', 'Program Manager', 'Development Coordinator', 'NGO Leader'],
      'legal': ['Attorney', 'Legal Counsel', 'Compliance Officer', 'Judge'],
      'engineering': ['Civil Engineer', 'Mechanical Engineer', 'Engineering Director', 'Lead Engineer']
    };

    const titles = categoryTitles[category] || ['Professional', 'Manager', 'Director', 'Specialist'];

    return Array.from({ length: 12 }, (_, i) => ({
      id: `mock-${category}-${i}`,
      firstName: `Alumni`,
      lastName: `${i + 1}`,
      headline: `${titles[i % titles.length]} ${category ? `specializing in ${category}` : ''}`,
      industry: category || 'Various',
      pictureUrl: null,
      publicProfileUrl: null,
      location: 'United States',
      category: category || 'general',
      school: 'Middlebury College',
      fromAPI: false
    }));
  };

  // Add an alumni to saved connections
  const saveAlumniToConnections = (alumni) => {
    // Check if already in connections
    if (!connections.some(conn => conn.id === alumni.id)) {
      const newConnection = {
        id: alumni.id,
        name: `${alumni.firstName} ${alumni.lastName}`,
        title: alumni.headline,
        avatar: alumni.pictureUrl || `https://ui-avatars.com/api/?name=${alumni.firstName.charAt(0)}${alumni.lastName.charAt(0)}`
      };

      setConnections(prev => [...prev, newConnection]);

      // Store in localStorage for persistence
      const savedConnections = JSON.parse(localStorage.getItem('saved_alumni_connections') || '[]');
      savedConnections.push(newConnection);
      localStorage.setItem('saved_alumni_connections', JSON.stringify(savedConnections));

      // Show feedback to user
      alert(`Added ${alumni.firstName} ${alumni.lastName} to your connections!`);
    } else {
      alert('This alumni is already in your connections.');
    }
  };

  // Authentication check
  useEffect(() => {
    // Set up our global flag if it doesn't exist
    if (typeof window !== 'undefined') {
      window.__LINKEDIN_AUTH_STATES = window.__LINKEDIN_AUTH_STATES || {};
    }

    // Load saved connections from localStorage
    const loadSavedConnections = () => {
      try {
        const savedConnections = JSON.parse(localStorage.getItem('saved_alumni_connections') || '[]');
        if (savedConnections.length > 0) {
          setConnections(savedConnections);
        }
      } catch (e) {
        console.error("Error loading saved connections:", e);
      }
    };

    loadSavedConnections();

    const checkAndUpdateAuthStatus = async () => {
      setIsVerifying(true);

      // First check URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');

      // If we have a code in the URL, we're in the callback process
      if (code && state) {
        // Update temporary state tracking mechanisms
        window.__LINKEDIN_AUTH_STATES.authStarted = true;
        localStorage.setItem('linkedin_auth_in_progress', 'true');

        // Proceed with token exchange
        const storedState = localStorage.getItem('linkedin_oauth_state');
        if (state === storedState) {
          try {
            await exchangeCodeForToken(code);
            // After exchange, verify with Firestore
            const verified = await verifyFirestoreToken();
            setIsVerified(verified);
            setIsLinkedInConnected(verified);

            // Handle verification failure after token exchange
            if (!verified) {
              console.warn("Token verification failed after code exchange");
              setError("Authentication succeeded but token verification failed. Please try again.");
            } else {
              // Success - load alumni data if verified
              loadAlumni(selectedCategory);
            }
          } catch (error) {
            console.error("Error in code exchange process:", error);
            setError("Failed to complete LinkedIn authentication. Please try again.");
            setIsVerified(false);
            setIsLinkedInConnected(false);
          }
        } else {
          setError('Invalid state parameter. Authentication failed.');
          setIsVerified(false);
          setIsLinkedInConnected(false);
        }

        // Clean URL regardless of outcome
        window.history.replaceState({}, document.title, window.location.pathname);
        setIsVerifying(false);
        return;
      }

      // For all other cases, verify the token in Firestore
      try {
        console.log("Checking token verification with Firestore...");
        const tokenStatus = await checkLinkedInTokenStatus();
        console.log("Token status check result:", tokenStatus);

        // If token exists but is expired, show error message
        if (tokenStatus.exists && tokenStatus.isExpired) {
          setError("Your LinkedIn connection has expired. Please connect again.");
          setIsVerified(false);
          setIsLinkedInConnected(false);
        } else if (tokenStatus.exists) {
          // Token exists and is valid
          setIsVerified(true);
          setIsLinkedInConnected(true);
          ensureLinkedInProfile();

          // Try to fetch profile
          const existingToken = localStorage.getItem('linkedin_access_token');
          if (existingToken) {
            fetchLinkedInProfile(existingToken);
          }

          // Load alumni data
          loadAlumni(selectedCategory);
        } else {
          // No token exists
          setIsVerified(false);
          setIsLinkedInConnected(false);
        }
      } catch (error) {
        console.error("Error verifying token:", error);
        setIsVerified(false);
        setIsLinkedInConnected(false);
      }

      setIsVerifying(false);
    };

    checkAndUpdateAuthStatus();
  }, [ensureLinkedInProfile, selectedCategory]);

  // Load alumni when category changes
  useEffect(() => {
    if (isVerified) {
      loadAlumni(selectedCategory);
    }
  }, [selectedCategory, isVerified]);

  // Token exchange function
  const exchangeCodeForToken = async (code) => {
    try {
      setIsConnecting(true);

      // Update state to ensure temporary UI is shown
      setIsLinkedInConnected(true);
      window.__LINKEDIN_AUTH_STATES.isAuthenticated = true;
      localStorage.setItem('linkedin_auth_complete', 'true');

      // Ensure we have a temporary profile even before API call
      ensureLinkedInProfile();

      // Force update to ensure UI reflects changes
      forceUpdate();

      // Add a small delay to ensure state updates are processed
      await new Promise(resolve => setTimeout(resolve, 100));

      try {
        // Call backend endpoint to exchange the code using the correct path
        const response = await fetch(`${API_BASE_URL}/api/linkedin/exchange-code`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code }),
        });

        console.log("Backend response status:", response.status, response.ok);

        if (response.ok) {
          try {
            const data = await response.json();
            console.log("Token data received:", data);

            if (data && data.accessToken) {
              localStorage.setItem('linkedin_access_token', data.accessToken);

              // Get user ID if available (e.g. from Firebase Auth)
              const userId = localStorage.getItem('user_id') || null;

              // Store token in backend for alumni scraper
              const storeResult = await storeLinkedInToken(data.accessToken, data.expiresIn, userId);
              console.log("Token store result:", storeResult);

              if (!storeResult) {
                console.warn("Failed to store token in backend");
                setError("Connected to LinkedIn but failed to store token for alumni search. Try again.");
                setIsVerified(false);
                return false;
              }

              // Try to fetch profile but don't wait for it
              fetchLinkedInProfile(data.accessToken).catch(() => {
                // Continue even if profile fetch fails
              });

              return true;
            }
          } catch (parseErr) {
            console.error("Error parsing token response:", parseErr);
          }
        }

        // If we get here, something went wrong with the token exchange
        setIsVerified(false);
        return false;

      } catch (fetchErr) {
        console.error("Error during token exchange:", fetchErr);
        setIsVerified(false);
        return false;
      }
    } catch (err) {
      setError(`Error during LinkedIn authentication: ${err.message}`);
      setIsVerified(false);
      return false;
    } finally {
      setIsConnecting(false);
    }
  };

  // Fetch profile function
  const fetchLinkedInProfile = async (accessToken) => {
    try {
      console.log("Attempting to fetch LinkedIn profile data...");

      // Call your backend endpoint that uses the access token to get profile data
      try {
        // Use the correct API endpoint path
        const response = await fetch(`${API_BASE_URL}/api/linkedin/profile`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        });

        console.log("Profile API response status:", response.status);

        if (response.ok) {
          try {
            const profileData = await response.json();
            console.log("Profile data received:", profileData);

            if (profileData && (profileData.firstName || profileData.lastName)) {
              setLinkedInProfile({
                firstName: profileData.firstName || 'LinkedIn',
                lastName: profileData.lastName || 'User',
                profilePicture: profileData.profilePicture || null,
                headline: profileData.headline || profileData.email || 'LinkedIn Member'
              });
              console.log("LinkedIn profile set with data:", profileData.firstName, profileData.lastName);
              return;
            } else {
              console.warn("Profile data incomplete:", profileData);
            }
          } catch (parseErr) {
            console.error("Error parsing profile data:", parseErr);
          }
        } else {
          console.error("Error fetching profile:", response.status);
          // Try to get error details
          try {
            const errorText = await response.text();
            console.error("Error details:", errorText);
          } catch (e) {
            console.error("Could not get error details");
          }
        }
      } catch (fetchErr) {
        console.error("Network error fetching profile:", fetchErr);
      }

      // If we get here, we need to ensure a fallback profile
      console.log("Using fallback profile due to fetch failure");
      if (!linkedInProfile) {
        setLinkedInProfile({
          firstName: 'LinkedIn',
          lastName: 'User',
          profilePicture: null,
          headline: 'Connected User - Profile Fetch Failed'
        });
      }
    } catch (err) {
      console.error('Error in profile fetch process:', err);
      // Ensure we have a profile even on error
      if (!linkedInProfile) {
        setLinkedInProfile({
          firstName: 'LinkedIn',
          lastName: 'User',
          profilePicture: null,
          headline: 'Error Retrieving Profile'
        });
      }
    }
  };

  // Handle LinkedIn connection
  const handleConnectLinkedIn = () => {
    setIsConnecting(true);
    setError(null); // Clear any previous errors

    // Set our global authentication flag
    if (typeof window !== 'undefined') {
      window.__LINKEDIN_AUTH_STATES = window.__LINKEDIN_AUTH_STATES || {};
      window.__LINKEDIN_AUTH_STATES.authStarted = true;
    }

    // Set localStorage flags for persistence
    localStorage.setItem('linkedin_auth_in_progress', 'true');

    const clientId = process.env.REACT_APP_LINKEDIN_CLIENT_ID || '783gvchr9ii0b8';

    const redirectUri = process.env.NODE_ENV === 'development'
      ? 'http://localhost:5002/networking/linkedin-callback'
      : 'https://middgpt.web.app/networking/linkedin-callback';

    // Request more permissions including r_network for connections
    const scope = encodeURIComponent('openid profile email r_network');

    // Generate a random state string for security
    const state = Math.random().toString(36).substring(2);
    localStorage.setItem('linkedin_oauth_state', state);

    // Set a flag to ensure we return to networking after auth
    sessionStorage.setItem('show_networking', 'true');

    const authUrl = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${scope}&state=${state}`;

    // Open in same window
    window.location.href = authUrl;
  };

  // Show loading state while verifying
  if (isVerifying) {
    return (
      <div className="flex-1 w-full max-w-3xl mx-auto h-full pt-16 pb-24 flex flex-col font-Poppins justify-center items-center">
        <div className="text-center">
          <div className="w-16 h-16 border-t-4 border-b-4 border-blue-500 rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-lg">Verifying LinkedIn connection...</p>
        </div>
      </div>
    );
  }

  // User is verified, show the networking UI
  if (isVerified) {
    return (
      <div className="flex-1 w-full mx-auto h-full flex">
        {/* Left sidebar with connections */}
        <div
          className={`${isOnPhone ? 'w-24' : 'w-64'} h-full border-r dark:border-[#2f2f2f] overflow-y-auto flex-shrink-0`}
        >
          {/* User profile */}
          {linkedInProfile && (
            <div className="px-4 py-4 border-b dark:border-[#2f2f2f]">
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-500 dark:text-blue-300 flex-shrink-0">
                  {linkedInProfile.profilePicture ? (
                    <img
                      src={linkedInProfile.profilePicture}
                      alt={`${linkedInProfile.firstName} ${linkedInProfile.lastName}`}
                      className="w-10 h-10 rounded-full"
                    />
                  ) : (
                    <span className="text-lg font-semibold">
                      {linkedInProfile.firstName.charAt(0)}{linkedInProfile.lastName.charAt(0)}
                    </span>
                  )}
                </div>
                {!isOnPhone && (
                  <div className="ml-3 overflow-hidden">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {linkedInProfile.firstName} {linkedInProfile.lastName}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {linkedInProfile.headline}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Connections list */}
          <div className="py-2 px-1">
            <h3 className="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Saved Connections
            </h3>
            {connections.length === 0 ? (
              <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                No saved connections yet. Browse alumni to add connections.
              </div>
            ) : (
              connections.map(connection => (
                <div
                  key={connection.id}
                  onClick={() => setSelectedConnection(connection)}
                  className={`px-3 py-2 flex items-center cursor-pointer transition-colors rounded-lg mt-1
                    ${selectedConnection?.id === connection.id
                      ? 'bg-gray-50 dark:bg-[#3e3e3e]'
                      : 'hover:bg-gray-100 dark:hover:bg-[#2e2e2e]'}
                  `}
                >
                  <img
                    src={connection.avatar}
                    alt={connection.name}
                    className="w-10 h-10 rounded-full flex-shrink-0"
                  />
                  {!isOnPhone && (
                    <div className="ml-3 overflow-hidden">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {connection.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {connection.title}
                      </p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Main content area */}
        <div className="flex-1 flex flex-col h-full overflow-hidden">
          {/* Tabs */}
          <div className="border-b dark:border-[#2f2f2f]">
            <div className="flex">
              <button
                className={`px-4 py-2 text-sm font-medium ${
                  activeTab === 'connections'
                    ? 'border-b-2 border-blue-500 text-blue-500 dark:text-blue-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
                onClick={() => setActiveTab('connections')}
              >
                Chat
              </button>
              <button
                className={`px-4 py-2 text-sm font-medium ${
                  activeTab === 'alumni'
                    ? 'border-b-2 border-blue-500 text-blue-500 dark:text-blue-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
                onClick={() => setActiveTab('alumni')}
              >
                Find Alumni
              </button>
            </div>
          </div>

          {/* Content based on active tab */}
          {activeTab === 'connections' ? (
            // Chat panel
            <ConnectionChatPanel
              selectedConnection={selectedConnection}
              onClose={onClose}
              isOnPhone={isOnPhone}
              linkedInProfile={linkedInProfile}
            />
          ) : (
            // Alumni browser
            <div className="flex-1 overflow-y-auto p-4">
              {/* Category selector */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Filter by category:
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-[#1e1e1e] text-gray-900 dark:text-white"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>
                      {cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Loading indicator */}
              {isLoadingAlumni && (
                <div className="flex justify-center my-8">
                  <div className="w-12 h-12 border-t-4 border-b-4 border-blue-500 rounded-full animate-spin"></div>
                </div>
              )}

              {/* Alumni grid */}
              {!isLoadingAlumni && (
                <>
                  <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                    Alumni in {selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)}
                  </h2>

                  {alumni.length === 0 ? (
                    <p className="text-gray-500 dark:text-gray-400">
                      No alumni found in this category. Try a different category.
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {alumni.map((person) => (
                        <div
                          key={person.id}
                          className="border dark:border-[#2f2f2f] rounded-lg p-4 bg-white dark:bg-[#1a1a1a] flex"
                        >
                          <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-500 dark:text-blue-300 flex-shrink-0">
                            {person.pictureUrl ? (
                              <img
                                src={person.pictureUrl}
                                alt={`${person.firstName} ${person.lastName}`}
                                className="w-12 h-12 rounded-full"
                              />
                            ) : (
                              <span className="text-lg font-semibold">
                                {person.firstName.charAt(0)}{person.lastName.charAt(0)}
                              </span>
                            )}
                          </div>

                          <div className="ml-4 flex-1">
                            <h3 className="text-md font-medium text-gray-900 dark:text-white">
                              {person.firstName} {person.lastName}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {person.headline}
                            </p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                              {person.location}
                            </p>
                            <div className="mt-2">
                              <button
                                onClick={() => saveAlumniToConnections(person)}
                                className="text-sm bg-blue-50 hover:bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 dark:text-blue-400 py-1 px-3 rounded-md transition-colors"
                              >
                                Add to Connections
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // User is not verified, show the LinkedIn connect screen
  return (
    <div className="flex-1 w-full max-w-3xl mx-auto h-full pt-16 pb-24 flex flex-col font-Poppins">
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center max-w-md p-8 bg-white dark:bg-[#1a1a1a] rounded-xl border dark:border-[#2f2f2f] shadow-md">
          {error && (
            <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm">
              {error}
            </div>
          )}
          <div className="mb-6">
            <svg className="w-16 h-16 mx-auto text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/>
            </svg>
          </div>

          <h2 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">Connect with LinkedIn</h2>
          <p className="mb-6 text-gray-600 dark:text-gray-300">
            Connect your LinkedIn profile to find alumni that match your interests and background.
          </p>

          <button
            onClick={handleConnectLinkedIn}
            disabled={isConnecting}
            className={`w-full bg-[#0077b5] hover:bg-[#006097] text-white font-semibold py-3 px-4 rounded-lg flex items-center justify-center ${isConnecting ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            {isConnecting ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Connecting...
              </>
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/>
                </svg>
                Connect with LinkedIn
              </>
            )}
          </button>

          <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
            We'll only use your LinkedIn profile data to find alumni connections.
          </p>
        </div>
      </div>
    </div>
  );
};

export default NetworkingEditor;